import { Router, RouterLink } from '@angular/router';
import { Component, computed, inject, output, signal } from '@angular/core';
import { translate, translateSignal, TranslocoDirective } from '@jsverse/transloco';
import { OverlayPanel } from '@shared/components/overlay-panel/overlay-panel';
import { MainMenuItem, MainRoutes } from '@app/app.model';
import { Avatar } from '@shared/components/avatar/avatar';
import { DocumentApi } from '@core/documents/services/document-api';
import { WebOperationCode } from '@core/role/role.model';
import { asapScheduler } from 'rxjs';
import { currentLanguageCode } from '@core/language/current-language';
import { cloneDeep } from 'lodash-es';
import { Menu } from '@shared/components/menu/menu';
import { TaMenuTrigger } from '@shared/components/menu/menu-trigger';
import { TaMenuItem } from '@shared/components/menu/menu.model';
import { NavbarState } from '@core/components/navbar/navbar-state';
import { NAVBAR_AVATAR_BUTTON_CLASS } from '@core/components/navbar/navbar-styles';
import { ClickStopPropagation } from '@shared/directives/click-stop-propagation';
import { IndexedDbNotificationApi } from '@core/notifications/services/indexed-db-notification-api';
import { BadgeCounter } from '@shared/components/badge/badge-counter';
import { ViewportResizeListener } from '@core/viewport/viewport-resize-listener';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { injectCanDoOperation, injectSelectableWo } from '@core/role/state/role.selectors';
import { injectLoggedOperatorQuery } from '@core/operators/state/operator.queries';
import { AuthApi } from '@core/auth/services/auth-api';
import { AppState } from '@core/state/services/app-state';
import { Icon } from '@shared/directives/icons/iconify';
import { TagIconToken } from '@shared/directives/icons/iconify-registry';

@Component({
  selector: 'ta-navbar',
  template: `
    <ng-container *transloco="let translate">
      <div class="flex-1 flex gap-2">
        <button class="menu-btn" type="button" (click)="menu.toggle($event)">
          <span class="h-7.5 icon-fw" taIcon="grid"></span>
        </button>
        <span class="hidden md:flex items-center text-tag-text font-normal text-3xl">
          {{ translate(this.navbarTitle()) }}
        </span>
        <img alt="logo" class="h-10 flex md:hidden" [src]="this.logo()" />
        <ta-overlay-panel #menu [dismissable]="true" [hidePopupCaret]="true">
          @if (menu.overlayVisible) {
            <div class="flex flex-col gap-4 overflow-auto" [style.max-height.px]="this.menuMaxHeight()">
              <div class="grid grid-cols-1 gap-2">
                @for (m of this.menuItems(); track m.text) {
                  <a
                    class="menu {{ m.iconCss }}"
                    queryParamsHandling="merge"
                    taClickStopPropagation
                    [href]="m.href"
                    [routerLink]="m.routerLink"
                    (auxclick)="this.handleMenuAuxClick($event, menu)"
                    (click)="menu.hide()"
                    (contextmenu)="this.handleContextMenuClick(menu)"
                  >
                    @if (m.icon) {
                      <div class="icon">
                        <span class="nav-icon h-6" [taIcon]="m.icon"></span>
                      </div>
                    }
                    <span class="text-tag-text">
                      {{ translate('webOperations.' + m.text) }}
                    </span>
                  </a>
                }
                <!-- Pulsante guida -->
                <div class="menu help" role="presentation" (click)="this.openHelp(); menu.hide()">
                  <div class="icon">
                    <span class="nav-icon h-6" taIcon="question"></span>
                  </div>
                  <span class="text-tag-text">
                    {{ translate('guide') }}
                  </span>
                </div>
              </div>
            </div>
          }
        </ta-overlay-panel>
      </div>
      <img alt="logo" class="h-10 hidden md:flex" [src]="this.logo()" />
      <div class="flex-1 flex justify-end gap-4">
        @if (this.aiChatEnabled()) {
          <button
            class="text-2xl rounded-lg relative text-tag-text hover:text-tag-action"
            type="button"
            [title]="translate('aiChat')"
            (click)="this.toggleAiChat.emit()"
          >
            <span class="h-9" taIcon="aiChat"></span>
          </button>
        }
        <button
          class="text-3xl rounded-lg relative text-tag-text hover:text-tag-action"
          type="button"
          [title]="translate('notifications')"
          (click)="this.toggleNotifications.emit()"
        >
          <span class="h-7.5 icon-inline icon-fw" taIcon="bell"></span>
          <div class="absolute bottom-7 left-2">
            <ta-badge-counter
              [count]="this.unreadNotificationsCount()"
              [maxLenght]="2"
              [type]="this.notificationsCounterType()"
            />
          </div>
        </button>
        <button
          class="nav-btn"
          type="button"
          [attr.data-action]="'createTicket'"
          [disabled]="!this.canCreateTicket()"
          [title]="translate('createTicket')"
          (click)="this.createTicket($event)"
        >
          <span class="h-7.5 icon-fw" taIcon="headset"></span>
        </button>
        <ta-menu [dataSource]="this.avatarMenuItems()">
          <button
            taMenuTrigger
            type="button"
            [attr.aria-label]="this.loggedOperator()?.opeNameSurname ?? ''"
            [class]="this.avatarButtonClass"
            [title]="this.loggedOperator()?.opeNameSurname ?? ''"
          >
            <ta-avatar
              class="hover:cursor-pointer"
              shape="circle"
              size="large"
              [alt]="this.loggedOperator()?.opeNameSurname ?? ''"
              [bgColor]="this.operatorBgColor()"
              [image]="this.profileImage()"
              [label]="this.operatorInitials()"
              [textColor]="this.operatorInitialsColor()"
            />
          </button>
        </ta-menu>
      </div>
    </ng-container>
  `,
  styleUrl: './navbar.css',
  host: { class: 'flex items-center justify-between p-2 pr-4 bg-tag-action/10 @container' },
  imports: [
    TranslocoDirective,
    OverlayPanel,
    RouterLink,
    Avatar,
    Menu,
    TaMenuTrigger,
    ClickStopPropagation,
    BadgeCounter,
    Icon
  ]
})
export class Navbar {
  private readonly appStateService = inject(AppState);
  private readonly authService = inject(AuthApi);
  private readonly router = inject(Router);
  private readonly documentService = inject(DocumentApi);
  private readonly navbarService = inject(NavbarState);
  private readonly notificationsService = inject(IndexedDbNotificationApi);
  private readonly viewportResizeService = inject(ViewportResizeListener);
  private readonly loggedOperatorQuery = injectLoggedOperatorQuery();
  private readonly selectableWebOperations = injectSelectableWo();
  private readonly myProfileLabel = translateSignal('myprofile');
  private readonly disconnectLabel = translateSignal('disconnect');

  private readonly icons: Partial<Record<WebOperationCode, TagIconToken>> = {
    Assets: 'tools',
    Dashboard: 'chartLine',
    Settings: 'cog',
    Calendar: 'calendarDays',
    Planner: 'users',
    Ticket: 'headset',
    IoT: 'towerBroadcast',
    Items: 'box',
    NonCompliance: 'nc',
    Consultations: 'search',
    Import: 'fileImport',
    BusinessPartner: 'userTie'
  };
  private readonly menuOrder: WebOperationCode[] = [
    'Assets',
    'Calendar',
    'Ticket',
    'Planner',
    'IoT',
    'Items',
    'NonCompliance',
    'Dashboard',
    'Consultations',
    'Settings',
    'Import',
    'BusinessPartner'
  ];

  protected readonly aiChatEnabled = injectCanDoOperation('display', 'TagAI');
  protected readonly avatarButtonClass = NAVBAR_AVATAR_BUTTON_CLASS;
  protected readonly menuMaxHeight = signal(window.innerHeight - 30);
  protected readonly menuItems = computed<MainMenuItem[]>(() => {
    return cloneDeep(this.selectableWebOperations())
      .map((r) => ({
        text: r.webOperationCode,
        icon: this.icons[r.webOperationCode],
        iconCss: r.webOperationCode.toLowerCase(),
        routerLink: [{ outlets: { modal: null, primary: [this.webOperationCodeToRouterLink(r.webOperationCode)] } }],
        href: this.webOperationCodeToRouterLink(r.webOperationCode)
      }))
      .sort((a, b) => {
        return this.menuOrder.indexOf(a.text) - this.menuOrder.indexOf(b.text);
      });
  });
  protected readonly loggedOperator = computed(() => this.loggedOperatorQuery.data());
  protected readonly canCreateTicket = injectCanDoOperation('creation', 'Ticket');
  protected readonly navbarTitle = this.appStateService.navbarTitleKey;
  protected readonly profileImage = computed(() => {
    const img = this.documentService.userProfileImg();
    return !img ? '' : img;
  });
  protected readonly operatorInitials = computed(() => {
    const o = this.loggedOperator();
    if (!o) {
      return '';
    }
    const { name, surname } = o;
    return !this.profileImage() ? name.charAt(0) + surname.charAt(0) : '';
  });
  protected readonly operatorBgColor = computed(() => {
    const profileImage = this.profileImage();
    if (profileImage) {
      return 'transparent';
    }
    return this.loggedOperator()?.color ?? '';
  });
  protected readonly operatorInitialsColor = computed(() => {
    // TODO Da valutare - Se implementiamo questa funzione dobbiamo adeguare anche pianificazione e calendario.
    // getContrastingTextColor(this.loggedOperator().color?.trim() ?? '#fff');
    return 'var(--tag-text-inverse)';
  });
  protected readonly logo = this.appStateService.logo;
  protected unreadNotificationsCount = this.notificationsService.unreadNotificationsCount;
  protected readonly notificationsCounterType = computed(() => {
    return this.notificationsService.hasUnseenNotifications() ? 'counter-alert' : 'counter';
  });
  protected readonly avatarMenuItems = computed<TaMenuItem[]>(() => [
    {
      label: this.myProfileLabel(),
      icon: 'userSolid',
      command: () => {
        this.navigateMyProfile();
      }
    },
    {
      label: this.disconnectLabel(),
      icon: 'rightFromBracket',
      command: () => {
        this.logout();
      }
    }
  ]);

  public readonly toggleNotifications = output<void>();
  public readonly toggleAiChat = output<void>();

  constructor() {
    explicitEffect([this.viewportResizeService.debouncedWindowResized], () => {
      this.menuMaxHeight.set(window.innerHeight - 30);
    });
  }

  protected navigateMyProfile(): void {
    this.router.navigate(['my-profile'], { queryParamsHandling: 'merge' });
  }

  protected logout(): void {
    this.authService.logout(true);
  }

  protected createTicket(e: MouseEvent): void {
    this.navbarService.createTicketPressed.set(e);
    // Sposto l'esecuzione al thread successivo per permettere l'eventuale scrittura di dati in localStorage
    asapScheduler.schedule(() => {
      this.router.navigate([{ outlets: { 'second-modal': ['ticket', 'new'] } }], { queryParamsHandling: 'merge' });
    });
  }

  protected openHelp(): void {
    // finché la guida non sarà disponibile in spagnolo e tedesco reindirizziamo all'inglese
    const activeLang = currentLanguageCode() !== 'it' ? 'en' : currentLanguageCode();
    const baseUrl = location.origin.includes('localhost')
      ? 'https://graftest.tagapplication.it/TagSystem'
      : location.origin;
    const navbarTitle = activeLang !== 'it' ? 'my-account' : translate(`wiki-url.${this.navbarTitle()}`);
    const wikiurl = `${baseUrl}/TagSystem/help/wiki/qs/${activeLang}/${navbarTitle}`;
    window.open(wikiurl, '_blank', 'height=800,width=450');
  }

  /**
   * Chiude il menu al task successivo: il browser apre la voce in una nuova scheda del browser solo dopo
   * `auxclick`, e chiudere prima toglierebbe il link dal DOM.
   */
  protected handleMenuAuxClick(e: MouseEvent, menu: OverlayPanel) {
    if (e.button !== 1) {
      return;
    }
    setTimeout(() => {
      menu.hide();
    });
  }

  protected handleContextMenuClick(menu: OverlayPanel) {
    window.addEventListener(
      'focusout',
      () => {
        menu.hide();
      },
      { once: true }
    );
  }

  private webOperationCodeToRouterLink(woc: WebOperationCode): MainRoutes {
    let result = woc.toLowerCase() as MainRoutes;
    switch (woc) {
      case 'NonCompliance':
        result = 'non-compliance';
        break;
      case 'IoT':
        result = 'monitoring';
        break;
      case 'Assets':
        result = 'asset';
        break;
      case 'Consultations':
        result = 'consultations';
        break;
      case 'BusinessPartner':
        result = 'business-partner';
        break;
    }
    return result;
  }
}
